export interface Caption {
  role: 'user' | 'pal';
  /** Full utterance so far; use `tailCaption` for the on-screen pill only. */
  text: string;
  final: boolean;
}

// How long a caption stays after Tavus marks it final (long enough to read a sentence).
export const CAPTION_CLEAR_DELAY_MS = 4000;
// Fallback if a turn never gets a `final` event (interruption, dropped message).
export const CAPTION_STALE_DELAY_MS = 6000;
// Speech accumulates for the whole turn; show only the tail so long answers don't grow over the avatar's face.
export const MAX_CAPTION_CHARS = 140;

export function tailCaption(text: string, max = MAX_CAPTION_CHARS): string {
  const trimmed = text.trim();
  if (trimmed.length <= max) return trimmed;
  const cut = trimmed.length - max;
  const tail = trimmed.slice(cut);
  // Drop the partial word at the cut so the caption starts on a word boundary.
  if (trimmed[cut - 1] === ' ' || tail[0] === ' ') return `…${tail.trimStart()}`;
  const firstSpace = tail.indexOf(' ');
  return `…${firstSpace >= 0 ? tail.slice(firstSpace + 1) : tail}`;
}

// Steady captions: words fill fixed lines, and a full page is replaced at once, so text never slides as it grows.
export const CAPTION_LINE_CHARS = 48;
export const CAPTION_LINES = 2;

/**
 * The lines to show for an utterance so far. Words wrap into lines of at most `lineChars`, or lines
 * `fits` accepts; only the current page of `lines` lines is shown, so new words append in place and
 * a page turns only when full.
 */
export function captionPage(text: string, lineChars: number | ((line: string) => boolean) = CAPTION_LINE_CHARS, lines = CAPTION_LINES): string[] {
  const fits = typeof lineChars === 'number' ? (line: string) => line.length <= lineChars : lineChars;
  const words = text.trim().split(/\s+/).filter(Boolean);
  const wrapped: string[] = [];
  for (const word of words) {
    const last = wrapped[wrapped.length - 1];
    if (last !== undefined && fits(`${last} ${word}`)) wrapped[wrapped.length - 1] = `${last} ${word}`;
    else wrapped.push(word);
  }
  if (wrapped.length === 0) return [];
  const page = Math.floor((wrapped.length - 1) / lines);
  return wrapped.slice(page * lines, page * lines + lines);
}

/**
 * Which lines fit the caption box as drawn: at most CAPTION_LINE_CHARS, and no wider than the box's
 * text area in its font. A narrow call pane (side panel open, small window) fits fewer than 48
 * characters, and the browser wrapped the extra words onto a third line. Null when it can't measure.
 */
export function captionLineFits(box: HTMLElement): ((line: string) => boolean) | null {
  const style = getComputedStyle(box);
  const width = box.clientWidth - parseFloat(style.paddingLeft || '0') - parseFloat(style.paddingRight || '0');
  if (!(width > 0)) return null;
  const context = document.createElement('canvas').getContext('2d');
  if (!context) return null;
  context.font = `${style.fontStyle} ${style.fontWeight} ${style.fontSize} ${style.fontFamily}`;
  return (line) => line.length <= CAPTION_LINE_CHARS && context.measureText(line).width <= width;
}

// Tavus sends `conversation.utterance.streaming` over Daily app-messages; `speech` is the
// accumulated text for the turn. Avatar turns arrive as "replica" (legacy) and/or "pal" with the same text.
export function parseCaption(message: any): Caption | null {
  const data = message?.data ?? message;
  if (data?.event_type !== 'conversation.utterance.streaming') return null;
  const { role, speech, final } = data.properties ?? {};
  if ((role !== 'user' && role !== 'pal' && role !== 'replica') || typeof speech !== 'string' || !speech.trim()) return null;
  return { role: role === 'user' ? 'user' : 'pal', text: speech.trim(), final: final === true };
}
